import { Comparative, DetailLeadRowData, Lead, LeadRowData, LeadTableRow, Quotation } from './leads.component.model';
import { TranslateService } from '@ngx-translate/core';
import { KeyValueLabel, NoValue } from '@tribeauto/ngx-mobilite-material';
import { formatLocaleDate } from '../../helpers/date.helper';
import { formatCurrency, formatNumber } from '@angular/common';
import { getCorrectInsuranceLabel } from '../../layout/recap-card/recap-card.mapper';
import { InsuranceResponse } from '../contract-action/contract-response.model';

export const leadToRowData = (lead: Lead, translateService: TranslateService, locale: string): LeadRowData => {
  const comparative = getMaxById(lead.comparatives);
  const quotation = getMaxById(comparative?.quotations);
  const monthlyPayment = quotation ? quotation.installmentAmount : 0;
  return {
    date:
      formatLocaleDate(new Date(lead.date)) ??
      new Date(new Date().getFullYear(), 0, 1).toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
    cckNumber: lead.reference,
    clientName: lead.prospectName,
    model: comparative?.vehicle.model ?? '',
    product: quotation?.productCode
      ? translateService.instant(`application.leads.product.${quotation.productCode.toLowerCase()}`)
      : '',
    monthlyPayment: formatNumber(monthlyPayment, locale, '1.0-0'),
    insurancesNumber: quotation ? quotation.insurances?.length || 0 : 0,
    details: getDetailsForRowData(comparative, quotation, locale, translateService),
    step: lead.step,
    createdBy: lead.createdBy ?? ''
  };
};

export const leadRowDataToTableRow = (rowData: LeadRowData, translateService: TranslateService): LeadTableRow => {
  return {
    tableData: Object.keys(rowData)
      .filter(key => !['details', 'step'].includes(key))
      .map(key => ({
        key: translateService.instant(`application.leads.table.${key}`),
        label: key === 'cckNumber' ? String(rowData[key as keyof LeadRowData]).split('-')[0] : undefined,
        value: String(rowData[key as keyof LeadRowData]),
        iconName: key === 'insurancesNumber' ? 'umbrella' : undefined,
        unit: key === 'monthlyPayment' ? '€' : undefined,
        isClickable: key === 'cckNumber',
        isLoading: key === 'createdBy'
      })),
    description: translateService.instant('application.leads.table.details.title'),
    details: getDetailsRowData(rowData.details, translateService),
    step: rowData.step
  };
};

const getDetailsForRowData = (
  comparative: Comparative | NoValue,
  quotation: Quotation | NoValue,
  locale: string,
  translateService: TranslateService
): DetailLeadRowData => {
  const subscribedInsurances =
    quotation?.insurances
      ?.map(insurance =>
        translateService.instant(getCorrectInsuranceLabel(insurance as InsuranceResponse) ?? insurance.category)
      )
      .join(', ') ?? '';

  return {
    brand: comparative?.vehicle?.brand ? comparative.vehicle.brand : '-',
    totalAmount: quotation?.totalAmount ? formatCurrency(quotation.totalAmount, locale, '€') : '- €',
    subscribedInsurances
  };
};

const getDetailsRowData = (details: DetailLeadRowData, translateService: TranslateService): KeyValueLabel[] => {
  return Object.keys(details).map(key => ({
    key,
    value: translateService.instant(`application.leads.table.details.${key}`),
    label: String(details[key as keyof DetailLeadRowData] ?? '')
  }));
};

const getMaxById = <T extends { id: number }>(items: T[] | NoValue): T | null => {
  if (!items?.length) {
    return null;
  }
  const ids = items.map(item => item.id);
  const maxId = Math.max(...ids);
  return items.find(item => item.id === maxId) ?? null;
};
